import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Chip,
  Divider,
  Button,
  Avatar,
  Grid,
  Paper
} from '@mui/material';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  ArrowBack as ArrowBackIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon
} from '@mui/icons-material';

/**
 * @component BlogPostPage
 * @description Single blog post page for Laprecio ethical mineral trading insights
 *
 * SEO Considerations:
 * - Implements proper meta tags with Helmet
 * - Uses semantic HTML structure with proper heading hierarchy
 * - Includes keyword-rich content about ethical mineral trading
 * - Provides social sharing options for better distribution
 * - Includes structured data for blog post
 * - Implements proper heading levels (h1, h2, h3)
 */
const BlogPostPage = () => {
  const { slug } = useParams();

  // State to track image loading errors
  const [imageError, setImageError] = useState(false);

  // Fallback image when the actual image fails to load
  const fallbackImage = 'https://i.imgur.com/YourImageID.png';

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  // Blog post data based on the slug parameter
  const blogPosts = {
    'ethical-minerals-guide': {
      title: 'How to Buy Ethical Minerals Directly from Global Miners: A Complete Guide',
      slug: 'ethical-minerals-guide',
      date: 'May 15, 2023',
      author: {
        name: 'Laprecio Team',
        avatar: 'https://i.imgur.com/YourImageID.png',
        bio: 'Experts in ethical mineral trading and blockchain verification'
      },
      image: '/images/blog/ethical-minerals-trading.jpg',
      imageAlt: 'Ethical mineral trading process showing direct sourcing from global miners',
      categories: ['ethical-sourcing', 'supply-chain'],
      tags: ['Ethical Sourcing', 'Global Minerals', 'Supply Chain', 'Blockchain Verification'],
      content: `
        <h2>Introduction</h2>
        <p>In today's global economy, sourcing minerals ethically is not just a moral imperative but increasingly a business necessity. With growing consumer awareness and tightening regulations, companies are seeking transparent and ethical supply chains for their mineral needs.</p>

        <p>The world holds rich mineral deposits, yet historically, accessing these resources ethically has been challenging. This guide will walk you through the process of sourcing minerals directly from global miners while ensuring ethical standards, regulatory compliance, and business efficiency.</p>

        <h2>Why Source Minerals Directly from Global Miners?</h2>

        <h3>Rich Mineral Diversity</h3>
        <p>The global mining industry offers an extraordinary wealth of mineral resources, including:</p>
        <ul>
          <li><strong>Copper</strong> (Chile, Peru, DRC, Zambia)</li>
          <li><strong>Cobalt</strong> (DRC, Russia, Australia)</li>
          <li><strong>Gold</strong> (China, Australia, Russia, USA)</li>
          <li><strong>Lithium</strong> (Australia, Chile, Argentina)</li>
          <li><strong>Manganese</strong> (South Africa, Australia, Brazil)</li>
          <li><strong>Rare Earth Elements</strong> (China, USA, Myanmar)</li>
        </ul>

        <h3>Economic Impact</h3>
        <p>Direct sourcing from global miners offers significant benefits:</p>
        <ul>
          <li><strong>Fair compensation</strong> for local miners and communities</li>
          <li><strong>Economic development</strong> in mining regions</li>
          <li><strong>Reduced costs</strong> by eliminating multiple intermediaries</li>
          <li><strong>Supply chain resilience</strong> through direct relationships</li>
        </ul>

        <h3>Ethical Considerations</h3>
        <p>Ethical sourcing addresses several critical concerns:</p>
        <ul>
          <li><strong>Conflict minerals</strong>: Ensuring minerals don't fund armed conflicts</li>
          <li><strong>Labor practices</strong>: Supporting mines with fair labor conditions</li>
          <li><strong>Environmental impact</strong>: Working with operations that minimize ecological damage</li>
          <li><strong>Community relations</strong>: Engaging with miners who maintain positive community relationships</li>
        </ul>

        <h2>Challenges in Direct Mineral Sourcing</h2>
        <p>Despite the benefits, several challenges have traditionally made direct sourcing difficult:</p>
        <ol>
          <li><strong>Verification challenges</strong>: Confirming the ethical status of mining operations</li>
          <li><strong>Logistics complexity</strong>: Navigating export procedures and transportation</li>
          <li><strong>Quality assurance</strong>: Ensuring consistent mineral quality</li>
          <li><strong>Payment security</strong>: Facilitating secure transactions across borders</li>
          <li><strong>Regulatory compliance</strong>: Meeting international standards and regulations</li>
        </ol>

        <h2>The Step-by-Step Process for Ethical Mineral Sourcing</h2>

        <h3>1. Define Your Requirements</h3>
        <p>Before beginning your sourcing journey:</p>
        <ul>
          <li><strong>Identify specific minerals</strong> needed for your operations</li>
          <li><strong>Determine quality specifications</strong> required for your applications</li>
          <li><strong>Establish volume requirements</strong> and delivery timelines</li>
          <li><strong>Set your ethical standards</strong> and compliance requirements</li>
        </ul>

        <h3>2. Find Verified Ethical Suppliers</h3>
        <p>Traditional methods of finding suppliers include:</p>
        <ul>
          <li>Industry trade shows and conferences</li>
          <li>Mining association directories</li>
          <li>Government mining departments</li>
          <li>Word-of-mouth referrals</li>
        </ul>
        <p><strong>Modern solution</strong>: Platforms like Laprecio provide a centralized marketplace where buyers can connect directly with verified ethical miners globally. These platforms handle verification, ensuring miners meet international ethical standards before joining.</p>
      `
    },
    'blockchain-mineral-supply-chains': {
      title: 'Blockchain Technology in Mineral Supply Chains: Beyond the Hype',
      slug: 'blockchain-mineral-supply-chains',
      date: 'June 10, 2023',
      author: {
        name: 'Dr. Sarah Chen',
        avatar: 'https://i.imgur.com/YourImageID.png',
        bio: 'Blockchain specialist with expertise in supply chain transparency'
      },
      image: '/images/blog/blockchain-verification.jpg',
      imageAlt: 'Blockchain verification process for ethical mineral supply chains',
      categories: ['blockchain', 'technology'],
      tags: ['Blockchain', 'Supply Chain', 'Technology', 'Verification', 'Digital Trust'],
      content: `
        <h2>Introduction</h2>
        <p>Blockchain technology has been heralded as a revolutionary force in supply chain management, particularly for industries like mineral trading where transparency and traceability are paramount. But beyond the buzzwords and hype, how is blockchain actually transforming mineral supply chains?</p>

        <p>This article explores the practical applications, real benefits, and ongoing challenges of implementing blockchain technology in global mineral supply chains, with a focus on how it enables the "Digital Trust" that is essential for ethical trading.</p>

        <h2>Understanding Blockchain in Supply Chains</h2>

        <h3>What Makes Blockchain Uniquely Suited for Mineral Traceability?</h3>
        <p>Blockchain technology offers several key features that make it particularly valuable for mineral supply chains:</p>
        <ul>
          <li><strong>Immutability</strong>: Once recorded, data cannot be altered, providing a permanent record of a mineral's journey</li>
          <li><strong>Decentralization</strong>: No single entity controls the entire system, reducing manipulation risks</li>
          <li><strong>Transparency</strong>: All authorized participants can view the same information in real-time</li>
          <li><strong>Smart Contracts</strong>: Automated execution of agreements when predefined conditions are met</li>
          <li><strong>Cryptographic Security</strong>: Advanced encryption protects sensitive data while allowing verification</li>
        </ul>

        <h3>Beyond Simple Tracking: The Digital Trust Framework</h3>
        <p>Blockchain creates a foundation for Digital Trust in mineral supply chains through:</p>
        <ul>
          <li><strong>Verified Identity</strong>: Cryptographic proof of who handled the minerals at each stage</li>
          <li><strong>Tamper-Evident Records</strong>: Any attempt to alter records is immediately detectable</li>
          <li><strong>Consensus Mechanisms</strong>: Multiple parties must agree on the validity of information</li>
          <li><strong>Temporal Stamping</strong>: Precise recording of when each transaction or transfer occurred</li>
          <li><strong>Audit Trails</strong>: Complete history available for regulatory compliance and verification</li>
        </ul>

        <h2>Real-World Applications in Mineral Supply Chains</h2>

        <h3>Mine-to-Market Traceability</h3>
        <p>Blockchain enables comprehensive tracking from extraction to end-user:</p>
        <ul>
          <li><strong>Origin Verification</strong>: Documenting exact mine location and extraction methods</li>
          <li><strong>Chain of Custody</strong>: Recording each transfer of ownership and physical movement</li>
          <li><strong>Processing Verification</strong>: Documenting refining and processing steps with timestamps</li>
          <li><strong>Transportation Logging</strong>: Tracking shipping routes, methods, and handlers</li>
          <li><strong>End-User Delivery</strong>: Confirming final delivery and product incorporation</li>
        </ul>

        <h3>Compliance and Certification</h3>
        <p>Blockchain streamlines regulatory compliance through:</p>
        <ul>
          <li><strong>Automated Reporting</strong>: Direct submission of required documentation to authorities</li>
          <li><strong>Standards Verification</strong>: Confirmation of adherence to international standards</li>
          <li><strong>Certification Integration</strong>: Digital incorporation of third-party certifications</li>
          <li><strong>Due Diligence Documentation</strong>: Comprehensive records for OECD and other frameworks</li>
          <li><strong>Real-Time Auditing</strong>: Continuous verification rather than periodic checks</li>
        </ul>

        <h2>Challenges and Limitations</h2>
        <p>Despite its promise, blockchain implementation in mineral supply chains faces several challenges:</p>
        <ol>
          <li><strong>The Oracle Problem</strong>: Ensuring that data entered into the blockchain is accurate in the first place</li>
          <li><strong>Technical Barriers</strong>: Implementation costs and technical expertise requirements</li>
          <li><strong>Standardization Issues</strong>: Lack of universal protocols across the industry</li>
          <li><strong>Scalability Concerns</strong>: Processing limitations for high-volume transaction systems</li>
          <li><strong>Regulatory Uncertainty</strong>: Evolving legal frameworks for blockchain-based verification</li>
        </ol>

        <h2>The Future: Integrated Digital Trust Systems</h2>

        <h3>Beyond Basic Blockchain</h3>
        <p>The next generation of mineral supply chain systems will integrate:</p>
        <ul>
          <li><strong>IoT Sensors</strong>: Automated data collection from physical monitoring devices</li>
          <li><strong>AI Analysis</strong>: Pattern recognition to identify anomalies and potential fraud</li>
          <li><strong>Digital Twins</strong>: Virtual representations of physical shipments updated in real-time</li>
          <li><strong>Interoperable Networks</strong>: Communication between different blockchain systems</li>
          <li><strong>Decentralized Finance</strong>: Integrated payment and escrow systems</li>
        </ul>

        <h3>Building Global Trade on Digital Trust</h3>
        <p>The ultimate vision is a mineral trading ecosystem where:</p>
        <ul>
          <li>Buyers can verify the complete history of any mineral purchase</li>
          <li>Miners receive fair compensation through transparent pricing</li>
          <li>Regulators can access real-time compliance information</li>
          <li>Consumers can trust the ethical sourcing claims of finished products</li>
          <li>Communities benefit from documented sustainable practices</li>
        </ul>
        <p>Platforms like Laprecio are at the forefront of this transformation, creating integrated systems that make "Global Trade, Digital Trust" not just a slogan, but a practical reality for the mineral industry.</p>
      `
    },
    'global-mineral-regulations': {
      title: 'Navigating Global Mineral Regulations: A Comprehensive Guide',
      slug: 'global-mineral-regulations',
      date: 'July 5, 2023',
      author: {
        name: 'James Wilson',
        avatar: 'https://i.imgur.com/YourImageID.png',
        bio: 'International trade compliance expert specializing in mineral regulations'
      },
      image: '/images/blog/regulatory-compliance.jpg',
      imageAlt: 'Regulatory compliance documentation for ethical mineral trading',
      categories: ['compliance', 'regulatory'],
      tags: ['Compliance', 'Regulations', 'Due Diligence', 'EU Regulations', 'Global Trade'],
      content: `
        <h2>Introduction</h2>
        <p>The global minerals trade operates within an increasingly complex regulatory landscape. Companies sourcing minerals must navigate a web of international, regional, and national regulations designed to ensure ethical sourcing, prevent conflict financing, and promote sustainability.</p>

        <p>This comprehensive guide examines the key regulatory frameworks affecting mineral supply chains worldwide and provides practical strategies for achieving and maintaining compliance while building digital trust in your operations.</p>

        <h2>Major Regulatory Frameworks</h2>

        <h3>EU Conflict Minerals Regulation</h3>
        <p>The European Union's regulation on responsible sourcing of minerals:</p>
        <ul>
          <li><strong>Scope</strong>: Covers tin, tantalum, tungsten, and gold (3TG) from conflict-affected and high-risk areas worldwide</li>
          <li><strong>Requirements</strong>: Mandatory due diligence for EU importers meeting volume thresholds</li>
          <li><strong>Implementation</strong>: Fully effective since January 1, 2021</li>
          <li><strong>Due Diligence Standard</strong>: Based on OECD Due Diligence Guidance</li>
          <li><strong>Reporting</strong>: Annual reporting to member state authorities</li>
        </ul>

        <h3>US Dodd-Frank Act Section 1502</h3>
        <p>The pioneering US legislation on conflict minerals:</p>
        <ul>
          <li><strong>Scope</strong>: Covers 3TG minerals from the Democratic Republic of Congo and adjoining countries</li>
          <li><strong>Requirements</strong>: Disclosure requirements for publicly traded companies</li>
          <li><strong>Implementation</strong>: Reporting requirements in effect since 2014</li>
          <li><strong>Enforcement</strong>: SEC oversight with potential penalties for non-compliance</li>
          <li><strong>Current Status</strong>: Enforcement partially limited but disclosure requirements remain</li>
        </ul>

        <h3>OECD Due Diligence Guidance</h3>
        <p>The international standard for responsible mineral supply chains:</p>
        <ul>
          <li><strong>Scope</strong>: All minerals from conflict-affected and high-risk areas</li>
          <li><strong>Framework</strong>: Five-step framework for risk-based due diligence</li>
          <li><strong>Adoption</strong>: Referenced by most national and regional regulations</li>
          <li><strong>Application</strong>: Entire mineral supply chain from mine to end-user</li>
          <li><strong>Updates</strong>: Periodically revised to address emerging challenges</li>
        </ul>

        <h3>Emerging Regulations</h3>
        <p>New frameworks shaping the future of mineral sourcing:</p>
        <ul>
          <li><strong>EU Battery Regulation</strong>: Comprehensive sustainability requirements for battery minerals</li>
          <li><strong>Corporate Sustainability Due Diligence Directive</strong>: Broader human rights and environmental due diligence</li>
          <li><strong>National Mandatory Human Rights Due Diligence Laws</strong>: Growing trend in European countries</li>
          <li><strong>Critical Minerals Initiatives</strong>: Strategic sourcing requirements in various jurisdictions</li>
          <li><strong>Carbon Border Adjustment Mechanisms</strong>: Emerging carbon-related trade measures</li>
        </ul>

        <h2>Building a Compliance Strategy</h2>

        <h3>Risk Assessment and Mapping</h3>
        <p>The foundation of effective compliance:</p>
        <ul>
          <li><strong>Supply Chain Mapping</strong>: Identifying all actors in your mineral supply chains</li>
          <li><strong>Country Risk Analysis</strong>: Assessing conflict and governance risks in source countries</li>
          <li><strong>Supplier Risk Assessment</strong>: Evaluating the compliance capabilities of suppliers</li>
          <li><strong>Material Risk Prioritization</strong>: Focusing resources on highest-risk minerals and sources</li>
          <li><strong>Regulatory Exposure Analysis</strong>: Determining which regulations apply to your operations</li>
        </ul>

        <h3>Due Diligence Implementation</h3>
        <p>Practical steps for meeting regulatory requirements:</p>
        <ul>
          <li><strong>Policy Development</strong>: Creating clear sourcing policies aligned with international standards</li>
          <li><strong>Supplier Engagement</strong>: Communicating requirements and building capacity</li>
          <li><strong>Information Collection</strong>: Gathering necessary documentation from the supply chain</li>
          <li><strong>Third-Party Verification</strong>: Utilizing audits and certification schemes</li>
          <li><strong>Corrective Action Management</strong>: Addressing identified non-conformances</li>
        </ul>

        <h2>Digital Trust and Regulatory Compliance</h2>

        <h3>Technology-Enabled Compliance</h3>
        <p>Leveraging digital tools for more effective compliance:</p>
        <ul>
          <li><strong>Blockchain Verification</strong>: Immutable records of compliance documentation</li>
          <li><strong>Digital Traceability Systems</strong>: End-to-end tracking of minerals and compliance data</li>
          <li><strong>Automated Risk Monitoring</strong>: Real-time alerts for emerging compliance issues</li>
          <li><strong>Integrated Reporting Tools</strong>: Streamlined generation of regulatory reports</li>
          <li><strong>Supplier Compliance Portals</strong>: Centralized platforms for information exchange</li>
        </ul>

        <h3>The Competitive Advantage of Compliance</h3>
        <p>How regulatory excellence creates business value:</p>
        <ul>
          <li><strong>Market Access</strong>: Meeting requirements for regulated markets</li>
          <li><strong>Brand Protection</strong>: Avoiding reputational damage from compliance failures</li>
          <li><strong>Customer Confidence</strong>: Providing verified assurance of ethical sourcing</li>
          <li><strong>Operational Efficiency</strong>: Streamlining processes through systematic compliance</li>
          <li><strong>Investor Relations</strong>: Meeting ESG expectations of financial stakeholders</li>
        </ul>

        <h2>The Future of Mineral Regulation</h2>
        <p>The regulatory landscape continues to evolve, with several key trends emerging:</p>
        <ol>
          <li><strong>Expanding Material Scope</strong>: Moving beyond 3TG to include battery minerals and others</li>
          <li><strong>Broader Due Diligence</strong>: Incorporating environmental impacts and climate considerations</li>
          <li><strong>Mandatory Requirements</strong>: Shifting from voluntary to compulsory compliance</li>
          <li><strong>Digital Verification</strong>: Greater reliance on technology-enabled compliance solutions</li>
          <li><strong>Harmonization Efforts</strong>: Initiatives to align requirements across jurisdictions</li>
        </ol>

        <p>Platforms like Laprecio are designed to help companies navigate this complex regulatory environment by providing integrated solutions that combine compliance management with digital trust verification, making "Global Trade, Digital Trust" a practical reality in the mineral industry.</p>
      `
    }
  };

  // Get the blog post data based on the slug
  const blogPost = blogPosts[slug] || blogPosts['ethical-minerals-guide'];

  // Generate structured data for the blog post
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blogPost.title,
    "image": "https://i.imgur.com/YourImageID.png",
    "datePublished": blogPost.date,
    "dateModified": blogPost.date,
    "author": {
      "@type": "Organization",
      "name": blogPost.author.name,
      "url": "https://laprecio.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Laprecio",
      "logo": {
        "@type": "ImageObject",
        "url": "https://i.imgur.com/YourImageID.png"
      }
    },
    "description": blogPost.slug === 'ethical-minerals-guide' ?
      "Learn how to source ethical minerals directly from global miners. This comprehensive guide covers verification, logistics, payment security, and regulatory compliance." :
      blogPost.slug === 'blockchain-mineral-supply-chains' ?
        "Explore how blockchain technology is transforming mineral supply chains with immutable verification, real-time tracking, and simplified compliance." :
        "Stay compliant with international regulations including EU Conflict Minerals Regulation, US Dodd-Frank Act, and OECD Due Diligence Guidance.",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://laprecio.com/blog/${blogPost.slug}`
    },
    "keywords": blogPost.tags.join(", ")
  };

  return (
    <>
      <Helmet>
        <title>{blogPost.title} | Laprecio Blog</title>
        <meta name="description" content={structuredData.description} />
        <meta name="keywords" content={blogPost.tags.join(", ")} />
        <link rel="canonical" href={`https://laprecio.com/blog/${blogPost.slug}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://laprecio.com/blog/${blogPost.slug}`} />
        <meta property="og:title" content={`${blogPost.title} | Laprecio Blog`} />
        <meta property="og:description" content={structuredData.description} />
        <meta property="og:image" content="https://i.imgur.com/YourImageID.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://laprecio.com/blog/${blogPost.slug}`} />
        <meta property="twitter:title" content={`${blogPost.title} | Laprecio Blog`} />
        <meta property="twitter:description" content={structuredData.description} />
        <meta property="twitter:image" content="https://i.imgur.com/YourImageID.png" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          component={RouterLink}
          to="/blog"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 4 }}
        >
          Back to Blog
        </Button>

        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              lineHeight: 1.3
            }}
          >
            {blogPost.title}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                src={blogPost.author.avatar}
                alt={blogPost.author.name}
                sx={{ width: 40, height: 40, mr: 1, bgcolor: 'primary.main' }}
              >
                {blogPost.author.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {blogPost.author.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {blogPost.date}
                </Typography>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ height: 24 }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {blogPost.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(234, 179, 8, 0.1)',
                    color: '#ca8a04'
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: { xs: 200, sm: 300, md: 400 },
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 6,
            bgcolor: imageError ? 'rgba(234, 179, 8, 0.1)' : 'inherit'
          }}
        >
          <Box
            component="img"
            src={imageError ? fallbackImage : blogPost.image}
            alt={blogPost.imageAlt}
            onError={handleImageError}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: imageError ? 'contain' : 'cover',
              p: imageError ? 4 : 0,
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Box
              className="blog-content"
              sx={{
                '& h2': {
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  mt: 6,
                  mb: 3,
                  color: '#333'
                },
                '& h3': {
                  fontSize: '1.4rem',
                  fontWeight: 'bold',
                  mt: 4,
                  mb: 2,
                  color: '#555'
                },
                '& p': {
                  mb: 3,
                  lineHeight: 1.7
                },
                '& ul, & ol': {
                  mb: 3,
                  pl: 3
                },
                '& li': {
                  mb: 1
                },
                '& strong': {
                  fontWeight: 'bold',
                  color: '#555'
                }
              }}
              dangerouslySetInnerHTML={{ __html: blogPost.content }}
            />

            <Paper
              sx={{
                p: 4,
                mt: 6,
                mb: 4,
                borderRadius: 2,
                bgcolor: 'rgba(234, 179, 8, 0.05)',
                border: '1px solid rgba(234, 179, 8, 0.2)',
                borderLeft: '4px solid #eab308'
              }}
            >
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Ready to transform your mineral sourcing?
              </Typography>
              <Typography variant="body1" paragraph>
                Join Laprecio today and connect directly with verified ethical miners globally.
              </Typography>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                color="primary"
                size="large"
                sx={{ borderRadius: 50, px: 4 }}
              >
                Register Now
              </Button>
            </Paper>

            <Divider sx={{ my: 6 }} />

            <Box sx={{ mb: 6 }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Share this article
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<FacebookIcon />}
                  sx={{ borderRadius: 50 }}
                >
                  Facebook
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TwitterIcon />}
                  sx={{ borderRadius: 50 }}
                >
                  Twitter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LinkedInIcon />}
                  sx={{ borderRadius: 50 }}
                >
                  LinkedIn
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  sx={{ borderRadius: 50 }}
                >
                  Email
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  About the Author
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={blogPost.author.avatar}
                    alt={blogPost.author.name}
                    sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}
                  >
                    {blogPost.author.name.charAt(0)}
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {blogPost.author.name}
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  {blogPost.author.bio}
                </Typography>
              </Paper>

              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Related Topics
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {blogPost.tags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      sx={{
                        bgcolor: 'rgba(234, 179, 8, 0.1)',
                        color: '#ca8a04'
                      }}
                    />
                  ))}
                </Box>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Subscribe to Our Newsletter
                </Typography>
                <Typography variant="body2" paragraph>
                  Stay updated with the latest insights about ethical mineral trading.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ borderRadius: 50 }}
                >
                  Subscribe
                </Button>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default BlogPostPage;
